import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { throwError } from 'rxjs/internal/observable/throwError';

import { Usuario } from '../../models/usuario.model';
import { environment } from 'src/environments/environment';
import { CONFIG, HTTPOPTIONS } from '../../common/constants';
import { Respuesta } from '../../models/respuesta';
import { BaseService } from '../base.service';

@Injectable({
  providedIn: 'root'
})
export class UsuarioService extends BaseService<Usuario> {
  private usuariosUrl = `${environment.apiBaseUrl}/usuarios`; // URL to web api

  constructor(public http: HttpClient) {
    super('UsuarioService');
  }

  /** Inicio Implementacion de CRUD-DB */
  public findAll(): Observable<Usuario[]> {
    return this.http.get<Usuario[]>(`${this.usuariosUrl}`).pipe(
      map(res => res['usuarios']),
      tap(_ => this.log('fetched usuarios')),
      catchError(this.handleError('getUsuarios'))
    );
  }

  public add(usuario: any): Observable<Respuesta> {
    return this.http.post<Respuesta>(this.usuariosUrl, usuario, HTTPOPTIONS).pipe(
      tap((res: Respuesta) => this.log(`added usuario | id=${res.id}`)),
      catchError(this.handleError('addUsuario'))
    );
  }

  public update(usuario: Usuario): Observable<Respuesta> {
    return this.http.put<Respuesta>(`${this.usuariosUrl}`, usuario, HTTPOPTIONS).pipe(
      tap((res: Respuesta) => this.log(`updated usuario | id=${res.id}`)),
      catchError(this.handleError('updateUsuario'))
    );
  }

  public delete(id: number): Observable<Respuesta> {
    const options = {
      headers: HTTPOPTIONS.headers,
      body: { id: id, idapp: CONFIG.IDAPP }
    };

    return this.http.delete<Respuesta>(`${this.usuariosUrl}`, options).pipe(
      tap((res: Respuesta) => this.log(`deleted usuario | id=${res.id}`)),
      catchError(this.handleError('deleteUsuario'))
    );
  }
  /** Fin Implementacion de CRUD-DB */

  public existingUsername(username: string): Observable<Respuesta> {
    return this.http.post<Respuesta>(`${this.usuariosUrl}/existing`, { usuario: username }, HTTPOPTIONS).pipe(
      tap(_ => this.log(`exists username => ${username}`)),
      catchError(this.handleError('existingUsername'))
    );
  }

  public existingEmail(email: string): Observable<Respuesta> {
    return this.http.post<Respuesta>(`${this.usuariosUrl}/existingemail`, { email: email }, HTTPOPTIONS).pipe(
      tap(_ => this.log(`exists email => ${email}`)),
      catchError(this.handleError('existingEmail'))
    );
  }

  public existingPassword(c_passwordactual: string): Observable<Respuesta> {
    return this.http.post<Respuesta>(`${this.usuariosUrl}/existingpassword`, { c_password: c_passwordactual }, HTTPOPTIONS).pipe(
      tap(_ => this.log(`exists password => ${c_passwordactual}`)),
      catchError(this.handleError('existingPassword'))
    );
  }

  public getTipoDocumento(): Observable<any> {
    return this.http.get<any>(`${this.usuariosUrl}/tipodocumento`).pipe(
      map(res => res['tipodocumento']),
      tap(_ => this.log('fetched tipodocumento')),
      catchError(this.handleError('getTipoDocumento'))
    );
  }

  public getTipoPersona(): Observable<any> {
    return this.http.get<any>(`${this.usuariosUrl}/tipopersona`).pipe(
      map(res => res['tipopersona']),
      tap(_ => this.log('fetched tipopersona')),
      catchError(this.handleError('getTipoPersona'))
    );
  }

  public updateFotoPerfil(datos: any): Observable<Respuesta> {
    return this.http.put<Respuesta>(`${this.usuariosUrl}/actualizarfoto`, datos, HTTPOPTIONS).pipe(
      tap((res: Respuesta) => this.log(`updated persona | id=${res.id}`)),
      catchError(this.handleError('updateFotoPerfil'))
    );
  }

  public getPerfilUsuario(): Observable<Respuesta> {
    return this.http.get<Respuesta>(`${this.usuariosUrl}/perfilusuario`).pipe(
      map(res => res['usuario']),
      tap(_ => this.log('fetched perfil usuario')),
      catchError(this.handleError('getPerfilUsuario'))
    );
  }

  public getPerfilInicio(): Observable<Respuesta> {
    return this.http.get<Respuesta>(`${this.usuariosUrl}/perfilinicio`).pipe(
      map(res => res['perfil']),
      tap(_ => this.log('fetched perfil usuario')),
      catchError(this.handleError('getPerfilUsuario'))
    );
  }

  public getPerfilAdmin(): Observable<Respuesta> {
    return this.http.get<Respuesta>(`${this.usuariosUrl}/perfiladmin`).pipe(
      map(res => res['perfil']),
      tap(_ => this.log('fetched perfil usuario')),
      catchError(this.handleError('getPerfilAdmin'))
    );
  }

  /*
  public getUsuarioMac(): Observable<Respuesta> {
    return this.http.get<Respuesta>(`${this.usuariosUrl}/usuariomac`).pipe(
      map(res => res['mac']),
      tap(_ => this.log('fetched mac')),
      catchError(this.handleError('getUsuarioMac'))
    );
  }
  */

  public actualizarPerfil(perfil: any): Observable<Respuesta> {
    return this.http.put<Respuesta>(`${this.usuariosUrl}/actualizar/perfil`, perfil, HTTPOPTIONS).pipe(
      tap((res: Respuesta) => this.log('updated datos perfil')),
      catchError(this.handleError('actualizarPerfil'))
    );
  }

  public updatePassword(password: string): Observable<Respuesta> {
    return this.http.put<Respuesta>(`${this.usuariosUrl}/actualizar/password`, { c_password: password }, HTTPOPTIONS).pipe(
      tap((res: Respuesta) => this.log('updated password usuario')),
      catchError(this.handleError('updatePassword'))
    );
  }

  public recuperarCuenta(cuenta: any): Observable<Respuesta> {
    return this.http.put<Respuesta>(`${this.usuariosUrl}/recuperar/cuenta`, cuenta, HTTPOPTIONS).pipe(
      tap((res: Respuesta) => this.log('recuperar cuenta del usuario')),
      catchError(this.handleError('recuperarCuenta'))
    );
  }

  public validarCorreo(email: string, tipo: number): Observable<Respuesta> {
    return this.http.post<Respuesta>(`${this.usuariosUrl}/validar/correo`, { c_email: email, c_tipo: tipo }, HTTPOPTIONS).pipe(
      tap((res: Respuesta) => this.log(`email | id=${res.id}`)),
      catchError(this.handleError('validarCorreo'))
    );
  }

  public validarCodigo(validar: any): Observable<Respuesta> {
    return this.http.post<Respuesta>(`${this.usuariosUrl}/validar/codigo`, validar, HTTPOPTIONS).pipe(
      tap((res: Respuesta) => this.log(`codigo | id=${res.id}`)),
      catchError((err: any) => {
        if (err.status == 504) return throwError({ message: 'Verifique su conexión de internet o consulte al proveedor de la pataforma.' })

        return throwError(err.error);
      })
    );
  }

  public registrarUserInicio(usuario: any): Observable<Respuesta> {
    return this.http.post<Respuesta>(`${this.usuariosUrl}/crearuser/inicio`, usuario, HTTPOPTIONS).pipe(
      tap((res: Respuesta) => this.log(`added usuario | id=${res.id}`)),
      catchError(this.handleError('registrarUserInicio'))
    );
  }

  public agregarCliente(cliente: any): Observable<Respuesta> {
    return this.http.post<Respuesta>(`${this.usuariosUrl}/agregar/cliente`, cliente, HTTPOPTIONS).pipe(
      tap((res: Respuesta) => this.log(`added cliente | id=${res.id}`)),
      catchError(this.handleError('agregarCliente'))
    );
  }

  public getClientes(): Observable<Respuesta> {
    return this.http.get<Respuesta>(`${this.usuariosUrl}/clientes`).pipe(
      map(res => res['clientes']),
      tap(_ => this.log('fetched clientes')),
      catchError(this.handleError('getClientes'))
    );
  }

  public getPerfilSuperAdmin(): Observable<Respuesta> {
    return this.http.get<Respuesta>(`${this.usuariosUrl}/perfilsuperadmin`).pipe(
      map(res => res['perfil']),
      tap(_ => this.log('fetched perfil usuario')),
      catchError(this.handleError('getPerfilSuperAdmin'))
    );
  }

  public actualizarPerfilSuperAdmin(perfil: any): Observable<Respuesta> {
    return this.http.put<Respuesta>(`${this.usuariosUrl}/actualizar/perfilsuperadmin`, perfil, HTTPOPTIONS).pipe(
      tap((res: Respuesta) => this.log('updated datos perfil')),
      catchError(this.handleError('actualizarPerfilSuperAdmin'))
    );
  }

  public getTipoPerfil(): Observable<any> {
    return this.http.get<any>(`${this.usuariosUrl}/tipoperfil`).pipe(
      map(res => res['tipoperfil']),
      tap(_ => this.log('fetched tipoperfil')),
      catchError(this.handleError('getTipoPerfil'))
    );
  }

  public getMonitoreoGrupo(): Observable<any> {
    return this.http.get<any>(`${this.usuariosUrl}/grupo`).pipe(
      map(res => res['grupo']),
      tap(_ => this.log('fetched grupos')),
      catchError(this.handleError('getMonitoreoGrupo'))
    );
  }

  public existingDocumentoCliente(documento: string): Observable<Respuesta> {
    return this.http.post<Respuesta>(`${this.usuariosUrl}/existing/documento/cliente`, { documento: documento }, HTTPOPTIONS).pipe(
      tap(_ => this.log(`exists documento => ${documento}`)),
      catchError(this.handleError('existingDocumentoCliente'))
    );
  }

  public existingDocumentoOperador(documento: string): Observable<Respuesta> {
    return this.http.post<Respuesta>(`${this.usuariosUrl}/existing/documento/operador`, { documento: documento }, HTTPOPTIONS).pipe(
      tap(_ => this.log(`exists documento => ${documento}`)),
      catchError(this.handleError('existingDocumentoOperador'))
    );
  }

  updatePersonaNatural(clienteNatural: any): Observable<any> {
    return this.http.post<any>(`${this.usuariosUrl}/cliente/natural/update`, clienteNatural, HTTPOPTIONS).pipe(
      tap((res: any) => this.handleMensaje(res.datamessage)),
      catchError(this.handleError1('updateClienteNatural'))
    );
  }

  updatePersonaJuridica(clienteNatural: any): Observable<any> {
    return this.http.post<any>(`${this.usuariosUrl}/cliente/juridica/update`, clienteNatural, HTTPOPTIONS).pipe(
      tap((res: any) => this.handleMensaje(res.datamessage)),
      catchError(this.handleError1('updateClienteNatural'))
    );
  }

  reporteMensual(data: any): Observable<any> {
    return this.http.post<any>(`${this.usuariosUrl}/cliente/reporte/mensual`, data, HTTPOPTIONS)
      .pipe(
        tap((res: any) => res.data),
        catchError(this.handleError1('reporteMensual'))
      );
  }

  // Reemplazando a validarCorreo
  public validarNuevoCorreo(email: string): Observable<any> {
    return this.http.post<any>(`${this.usuariosUrl}/validarnuevocorreo`, { email }, HTTPOPTIONS).pipe(
      tap((res: any) => this.log(`email | id=${res}`)),
      catchError((err: any) => {
        if (err.status == 504) return throwError({ message: 'Verifique su conexión de internet o consulte al proveedor de la pataforma.' })

        return throwError(err.error);
      })
    );
  }

  public crearCuentaComunidad(usuario: any): Observable<Respuesta> {
    return this.http.post<Respuesta>(`${this.usuariosUrl}/cuentacomunidad`, usuario, HTTPOPTIONS)
      .pipe(
        tap((res: Respuesta) => this.log(`crear Cuenta Demo | id=${res.id}`)),
        catchError((err: any) => {
          if (err.status == 504) return throwError({ message: 'Verifique su conexión de internet o consulte al proveedor de la pataforma.' })

          return throwError(err.error);
        })
      );
  }

  public updateprofile(perfil: any): Observable<any> {
    return this.http.put<any>(`${this.usuariosUrl}/profile`, perfil, HTTPOPTIONS)
      .pipe(
        catchError((err: any) => {
          if (err.status == 504) return throwError({ message: 'Verifique su conexión de internet o consulte al proveedor de la pataforma.' })

          return throwError(err.error);
        })
      );
  }
}
